import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '../icons';
import { IconButton } from './Button';

const GMAPS_KEY = 'AIzaSyCfICV0UAN7VbCsj5coBf_HJM5Gnv5MOH8';
const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

export default function AddressInput({ value, onChange, placeholder, className, required, readOnly = false }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [svFailed, setSvFailed] = useState(false);
  const hasAddress = value && value.trim().length > 5;
  // 640x400 (the free-tier max width) — the popup renders at up to 420
  // CSS px, so this stays sharp on retina/mobile screens too.
  const svUrl = hasAddress ? `https://maps.googleapis.com/maps/api/streetview?size=640x400&location=${encodeURIComponent(value)}&key=${GMAPS_KEY}` : null;
  const svThumb = hasAddress ? `https://maps.googleapis.com/maps/api/streetview?size=80x80&location=${encodeURIComponent(value)}&key=${GMAPS_KEY}` : null;
  useEffect(() => {
    // No Places autocomplete in read-only mode — the field isn't editable,
    // but the Street View thumbnail + Maps/Zillow links still work so they
    // stay available after Save (view mode), per Jaylen 2026-06-29.
    if (readOnly) return;
    if (!inputRef.current || !window.google?.maps?.places || autocompleteRef.current) return;
    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address']
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (place?.formatted_address) {
        onChange(place.formatted_address);
        setShowPreview(false);
      }
    });
    autocompleteRef.current = ac;
    return () => { if (autocompleteRef.current) google.maps.event.clearInstanceListeners(autocompleteRef.current); };
  }, []);
  return (
    // In read-only mode the address sits inside the client modal's
    // pointer-events-none view wrapper — re-enable pointer events here so the
    // Street View thumbnail + Google Maps / Zillow links stay clickable.
    <div className="relative" style={readOnly ? { pointerEvents: 'auto' } : undefined}>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="text" readOnly={readOnly} className={className || inputCls} value={value || ''} onChange={e => onChange?.(e.target.value)} placeholder={placeholder || 'Start typing an address...'} required={required} style={{flex:1}} />
        {hasAddress && (
          <button type="button" onClick={() => setShowPreview(p => !p)} className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-red-400 transition-colors" title="Street View preview">
            <img src={svThumb} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
          </button>
        )}
      </div>
      {/* Preview card. Two presentations (Jaylen 2026-07-29 — round 2 of
          the mobile-scale fix: the anchored dropdown hangs off whichever
          side the input doesn't reach, because it's positioned against
          the FIELD, not the screen):
            • Phones (<640px): portal'd to <body> as a centered card over
              a dim backdrop — screen-positioned, can't overflow anywhere.
            • Desktop: the familiar dropdown anchored under the field
              (kept `absolute` on purpose — inside the transformed <Modal>
              a `fixed` element would pin to the modal, CLAUDE.md).
          The image keeps its 8:5 shape at any width. */}
      {showPreview && hasAddress && (() => {
        const isNarrow = typeof window !== 'undefined' && window.innerWidth < 640;
        const card = (
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden w-full" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
              <span className="text-xs font-medium text-slate-600 truncate flex-1 min-w-0">{value}</span>
              <IconButton size="sm" label="Close preview" className="-m-1" onClick={() => setShowPreview(false)}><IconX size={14}/></IconButton>
            </div>
            {svFailed ? (
              <div className="p-8 text-center text-slate-400 text-sm">No Street View available for this address</div>
            ) : (
              <img src={svUrl} alt="Street View" className="w-full" style={{ aspectRatio: '8 / 5', objectFit: 'cover' }} onError={() => setSvFailed(true)} />
            )}
            <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-4">
              <a href={`https://www.google.com/maps?q=${encodeURIComponent(value)}`} target="_blank" rel="noopener" className="text-xs text-red-600 hover:text-red-700 font-medium">Open in Google Maps &rarr;</a>
              {/* Zillow search-by-address ("_rb" = results-by-address). No API/key
                  needed — lands on the property page when Zillow has the address,
                  otherwise on Zillow's search results. We can't know which without
                  pulling Zillow data (the thing we're deliberately avoiding), so
                  it's framed as a "Search" and the estimator falls back to manual
                  entry when nothing's there. */}
              <a href={`https://www.zillow.com/homes/${encodeURIComponent(value)}_rb/`} target="_blank" rel="noopener" data-testid="zillow-link" title="Opens a Zillow search for this address — not every property is listed" className="text-xs text-red-600 hover:text-red-700 font-medium">Search on Zillow &rarr;</a>
            </div>
          </div>
        );
        if (isNarrow) {
          return createPortal((
            <div className="fixed inset-0 z-[250] bg-black/40 flex items-center justify-center p-3"
              onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}>
              {card}
            </div>
          ), document.body);
        }
        return (
          <div className="absolute right-0 top-full mt-2 z-50" style={{ width: 'min(420px, calc(100vw - 24px))' }}>
            {card}
          </div>
        );
      })()}
    </div>
  );
}
